import { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../../components/ui/dialog";
import { useAuth } from "../../context/AuthContext";
import { openBackendFile } from "../../lib/download";
import { toast } from "sonner";
import { Plus, FileText, Download, Trash2 } from "lucide-react";

export default function Policies() {
  const { user } = useAuth();
  const canManage = ["partner","admin"].includes(user?.role);
  const [list, setList] = useState([]);
  const [form, setForm] = useState({ title: "", description: "", file: null });
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const load = () => api.get("/policies").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.file) { toast.error("Pick a PDF file"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", form.file);
      const url = `/policies?title=${encodeURIComponent(form.title)}${form.description ? `&description=${encodeURIComponent(form.description)}` : ""}`;
      await api.post(url, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Policy uploaded");
      setForm({ title: "", description: "", file: null });
      if (fileRef.current) fileRef.current.value = "";
      setOpen(false); load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete policy?")) return;
    try { await api.delete(`/policies/${id}`); toast.success("Deleted"); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const view = async (path) => {
    try { await openBackendFile(path); }
    catch (e) { toast.error("Cannot open"); }
  };

  return (
    <div className="space-y-6 fade-in" data-testid="policies-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="eyebrow">Firm Policies</div>
          <h1 className="font-sans text-4xl mt-2">Reference documents</h1>
          <p className="text-slate-600 text-sm mt-1">HR, leave, expense and code-of-conduct policies — accessible to all employees.</p>
        </div>
        {canManage && <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-slate-900 hover:bg-slate-800 rounded-sm" data-testid="add-policy-btn"><Plus className="h-4 w-4 mr-2"/>Upload Policy</Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader><DialogTitle className="font-sans text-2xl">Upload Policy PDF</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-4">
              <div><Label className="eyebrow">Title</Label><Input required value={form.title} onChange={(e)=>setForm({...form, title: e.target.value})} className="mt-2 rounded-sm" data-testid="policy-title"/></div>
              <div><Label className="eyebrow">Description (optional)</Label><Textarea rows={2} value={form.description} onChange={(e)=>setForm({...form, description: e.target.value})} className="mt-2 rounded-sm"/></div>
              <div>
                <Label className="eyebrow">PDF File</Label>
                <Input ref={fileRef} type="file" accept="application/pdf" onChange={(e)=>setForm({...form, file: e.target.files?.[0] || null})} className="mt-2 rounded-sm" required data-testid="policy-file"/>
              </div>
              <DialogFooter><Button type="submit" disabled={busy} className="bg-slate-900 hover:bg-slate-800 rounded-sm" data-testid="policy-save-btn">{busy ? "Uploading..." : "Upload"}</Button></DialogFooter>
            </form>
          </DialogContent>
        </Dialog>}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 stagger" data-testid="policies-grid">
        {list.length === 0 && <div className="col-span-3 bg-white border border-slate-200 rounded-sm p-12 text-center text-slate-500">No policies uploaded yet.</div>}
        {list.map((p) => (
          <div key={p.id} className="bg-white border border-slate-200 rounded-sm p-6 hover:shadow-md transition-shadow">
            <FileText className="h-7 w-7 text-[#C2A671] mb-4"/>
            <div className="font-sans text-xl">{p.title}</div>
            {p.description && <p className="text-sm text-slate-600 mt-2 line-clamp-2">{p.description}</p>}
            <div className="text-xs text-slate-400 mt-3">Uploaded {p.created_at?.slice(0,10)} by {p.uploaded_by_name || "—"}</div>
            <div className="mt-4 flex items-center gap-2 pt-4 border-t border-slate-100">
              <Button onClick={()=>view(p.storage_path)} variant="outline" size="sm" className="rounded-sm border-slate-300" data-testid={`policy-view-${p.id}`}><Download className="h-3.5 w-3.5 mr-1.5"/>View / Download</Button>
              {canManage && <button onClick={()=>remove(p.id)} className="ml-auto text-slate-400 hover:text-red-600" data-testid={`policy-delete-${p.id}`}><Trash2 className="h-4 w-4"/></button>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
